import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import {
  ExpandMoreOutlined,
  WorkspacesOutlined,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { getTenantId } from "../../utils/jwtUtils";
import {
  formatOrgMenuPrimary,
  formatOrgMenuSecondary,
  formatSelectedOrgLabel,
} from "../../utils/orgSwitcher";

const NAV_ICON_SX = { fontSize: 22 } as const;

interface OrgSwitcherProps {
  collapsed?: boolean;
  variant?: "sidebar" | "compact";
}

function OrgMenuItems({
  tenants,
  effectiveTenantId,
  onSelect,
}: {
  tenants: ReturnType<typeof useAuth>["tenants"];
  effectiveTenantId: string | null;
  onSelect: (tenantId: string) => void;
}) {
  return tenants.map((tenant) => {
    const secondary = formatOrgMenuSecondary(tenant);
    const isActive = tenant.tenantId === effectiveTenantId;
    return (
      <MenuItem
        key={tenant.tenantId}
        selected={isActive}
        onClick={() => onSelect(tenant.tenantId)}
      >
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {formatOrgMenuPrimary(tenant)}
          </Typography>
          {secondary && (
            <Typography variant="caption" color="text.secondary">
              {secondary}
            </Typography>
          )}
        </Box>
      </MenuItem>
    );
  });
}

const OrgSwitcher: React.FC<OrgSwitcherProps> = ({
  collapsed = false,
  variant = "sidebar",
}) => {
  const {
    token,
    tenants,
    activeTenantId,
    switchingTenant,
    tenantsLoading,
    tenantsLoadError,
    switchTenant,
    refreshTenantMemberships,
    error,
  } = useAuth();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const effectiveTenantId = activeTenantId ?? getTenantId();
  const currentLabel = formatSelectedOrgLabel(effectiveTenantId, tenants);
  const showSwitcher = tenants.length > 1;
  const showLoadingState =
    Boolean(token) && (tenantsLoading || (tenants.length === 0 && !tenantsLoadError));
  const showRetryState = Boolean(token) && Boolean(tenantsLoadError) && !tenantsLoading;

  if (!token && !showLoadingState && !showRetryState) {
    return null;
  }

  if (!showSwitcher && !showLoadingState && !showRetryState) {
    return null;
  }

  const handleSelect = (nextTenantId: string) => {
    setMenuAnchor(null);
    if (!nextTenantId || nextTenantId === effectiveTenantId || switchingTenant) {
      return;
    }
    void switchTenant(nextTenantId).catch(() => {
      // Error state is handled in AuthContext.
    });
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    handleSelect(event.target.value);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (switchingTenant) return;
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  if (showLoadingState && !showSwitcher) {
    if (variant === "compact") {
      return <CircularProgress size={20} sx={{ mx: 0.5 }} />;
    }
    if (collapsed) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      );
    }
    return (
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Skeleton variant="text" width="40%" height={16} sx={{ mb: 0.5 }} />
        <Skeleton variant="rounded" height={36} />
      </Box>
    );
  }

  if (showRetryState && !showSwitcher) {
    const retryControl = (
      <Button
        size="small"
        onClick={() => void refreshTenantMemberships()}
        sx={{ textTransform: "none" }}
      >
        Retry
      </Button>
    );
    if (variant === "compact") {
      return retryControl;
    }
    return (
      <Box sx={{ px: collapsed ? 0.5 : 2, pb: 1.5, textAlign: collapsed ? "center" : "left" }}>
        <Alert severity="warning" sx={{ py: 0, mb: collapsed ? 0.5 : 1 }}>
          {tenantsLoadError}
        </Alert>
        {retryControl}
      </Box>
    );
  }

  if (variant === "compact") {
    return (
      <>
        <Button
          size="small"
          onClick={openMenu}
          disabled={switchingTenant}
          endIcon={<ExpandMoreOutlined />}
          sx={{
            textTransform: "none",
            maxWidth: 140,
            color: "text.primary",
            mr: 0.5,
          }}
        >
          <Typography noWrap variant="body2" fontWeight={500}>
            {currentLabel}
          </Typography>
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <OrgMenuItems
            tenants={tenants}
            effectiveTenantId={effectiveTenantId}
            onSelect={handleSelect}
          />
        </Menu>
      </>
    );
  }

  if (collapsed) {
    return (
      <>
        <Tooltip title={currentLabel} placement="right">
          <IconButton
            size="small"
            onClick={openMenu}
            disabled={switchingTenant}
            aria-label={`Switch organization (${currentLabel})`}
            sx={{ display: "flex", mx: "auto", my: 0.5 }}
          >
            <WorkspacesOutlined sx={NAV_ICON_SX} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <OrgMenuItems
            tenants={tenants}
            effectiveTenantId={effectiveTenantId}
            onSelect={handleSelect}
          />
        </Menu>
      </>
    );
  }

  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", color: "text.secondary", mb: 0.5 }}
      >
        Organization
      </Typography>
      <FormControl fullWidth size="small">
        <Select
          value={effectiveTenantId ?? ""}
          onChange={handleChange}
          displayEmpty
          disabled={switchingTenant}
          renderValue={() => currentLabel}
          sx={{
            fontSize: "0.875rem",
            "& .MuiSelect-select": { py: 1 },
          }}
        >
          {tenants.map((tenant) => {
            const secondary = formatOrgMenuSecondary(tenant);
            return (
              <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {formatOrgMenuPrimary(tenant)}
                  </Typography>
                  {secondary && (
                    <Typography variant="caption" color="text.secondary">
                      {secondary}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {(error || tenantsLoadError) && !switchingTenant && (
        <Alert severity="error" sx={{ mt: 1, py: 0 }}>
          {error ?? tenantsLoadError}
        </Alert>
      )}
      {tenantsLoadError && (
        <Button
          size="small"
          onClick={() => void refreshTenantMemberships()}
          sx={{ mt: 0.5, textTransform: "none", px: 0 }}
        >
          Retry loading organizations
        </Button>
      )}
    </Box>
  );
};

export default OrgSwitcher;
